import { createClient } from "@supabase/supabase-js";
import { getSupabaseEnvironment } from "@/lib/supabaseEnvironment";
import {
  getStoredImpersonationAccessToken,
  getStoredImpersonationSessionId,
} from "@/lib/impersonationContext";

const url     = process.env.REACT_APP_SUPABASE_URL;
const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabaseEnv = getSupabaseEnvironment(url);
const authStorageScope = (supabaseEnv.host || "unknown")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "_")
  .replace(/^_+|_+$/g, "");

if (!url || !anonKey) {
  // Fail fast in dev — missing env almost always means a fresh checkout
  // that hasn't seen /app/frontend/.env yet.
  // eslint-disable-next-line no-console
  console.error("[supabaseClient] Missing REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY");
}

// The Emergent devtools script in public/index.html wraps window.fetch to
// log network calls, which consumes the response body and breaks supabase-js
// with "body stream already read" / DataCloneError. Pull an unwrapped fetch
// from a hidden iframe so supabase traffic bypasses the wrapper entirely.
function unwrappedFetch() {
  if (typeof document === "undefined") return fetch;
  try {
    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    frame.setAttribute("tabindex", "-1");
    frame.style.display = "none";
    (document.body || document.documentElement).appendChild(frame);
    const cw = frame.contentWindow;
    if (!cw?.fetch) return fetch;
    return cw.fetch.bind(cw);
  } catch {
    return fetch;
  }
}

function impersonationFetch(input, init = {}) {
  const baseFetch = unwrappedFetch();
  return baseFetch(input, withImpersonationHeader(init));
}

export function withImpersonationHeader(init = {}) {
  const sessionId = getStoredImpersonationSessionId();
  if (!sessionId) return init;
  const headers = new Headers(init.headers || {});
  const accessToken = getStoredImpersonationAccessToken();
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  headers.set("x-impersonation-session-id", sessionId);
  return { ...init, headers };
}

export const supabase = createClient(url, anonKey, {
  auth: {
    persistSession:    true,
    autoRefreshToken:  true,
    detectSessionInUrl: true,
    storageKey:        `dos_v2_auth_${supabaseEnv.tone}_${authStorageScope || "unknown"}`,
  },
  global: {
    fetch: impersonationFetch,
  },
});
